/**
 * ImageModelSelector Component
 *
 * Dropdown selector with CivitAI search for Fooocus checkpoint models.
 * Shows curated local models and searchable CivitAI catalog with previews.
 *
 * Features:
 * - Dropdown with search input (300ms debounce)
 * - Curated local models with thumbnails, stats, orientation badges
 * - CivitAI search results (catalog-only, non-selectable)
 * - Selected model detail card below the selector
 * - Mobile-first responsive design
 * - Close on outside click
 */

import { useEffect, useRef, useCallback, memo, useReducer } from 'react';
import { createPortal } from 'react-dom';
import { Box, Loader2, ChevronDown } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { imageGenerationAPI } from '../../services/imageGenerationAPI';
import type { ImageModelInfo } from '../../types';
import {
  imageModelSelectorReducer,
  initialState,
} from './model-selector/imageModelSelectorReducer';
import { PreviewPanel, SearchPanel, useModelDropdownPanel } from '../common/model-picker';

interface ImageModelSelectorProps {
  value: string | undefined;
  /**
   * Called when the selected checkpoint changes. The second argument carries
   * the full model record (when known) so the parent can apply per-model
   * recommendations such as steps / CFG (e.g. Hyper checkpoints need ~6 steps
   * at low CFG instead of the standard ~25 at CFG 7).
   */
  onChange: (model: string, meta?: ImageModelInfo) => void;
  provider: string;
  disabled?: boolean;
  className?: string;
}

/**
 * ImageModelSelector - Dropdown selector with CivitAI search
 */
export const ImageModelSelector = memo(function ImageModelSelector({
  value,
  onChange,
  provider,
  disabled = false,
  className = '',
}: ImageModelSelectorProps) {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(imageModelSelectorReducer, initialState);
  const { isOpen, panelStyle, triggerRef, panelRef, toggle, close } =
    useModelDropdownPanel(disabled);

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch local models when provider changes
  useEffect(() => {
    let cancelled = false;

    const fetchModels = async () => {
      dispatch({ type: 'MODELS_LOADING' });

      try {
        const response = await imageGenerationAPI.getModels(provider);

        if (cancelled) return;

        if (response.success && response.data) {
          const modelsData = response.data.models;
          dispatch({
            type: 'MODELS_LOADED',
            models: Array.isArray(modelsData) ? modelsData : [],
            defaultModel: response.data.default,
            supportsModels: response.data.supports_models,
          });

          const defaultModelId = response.data.default;
          if (!value && defaultModelId) {
            const defaultModel = (Array.isArray(modelsData) ? modelsData : []).find(
              (m) => m.id === defaultModelId
            );
            onChange(defaultModelId, defaultModel);
          }
        } else {
          dispatch({ type: 'MODELS_ERROR', error: response.error || 'Failed to load models' });
        }
      } catch {
        if (!cancelled) {
          dispatch({ type: 'MODELS_ERROR', error: 'Failed to load models' });
        }
      }
    };

    fetchModels();

    return () => {
      cancelled = true;
    };
  }, [provider, onChange, value]);

  // Load popular CivitAI models when dropdown first opens
  useEffect(() => {
    if (!isOpen || state.popularLoaded) return;

    let cancelled = false;
    const loadPopular = async () => {
      try {
        const response = await imageGenerationAPI.searchModels('', 20);
        if (!cancelled && response.success && response.data) {
          const popularData = response.data.models;
          dispatch({
            type: 'POPULAR_LOADED',
            models: Array.isArray(popularData) ? popularData : [],
          });
        } else if (!cancelled) {
          dispatch({ type: 'POPULAR_LOADED', models: [] });
        }
      } catch {
        // Silently fail - popular models are supplementary
        if (!cancelled) {
          dispatch({ type: 'POPULAR_LOADED', models: [] });
        }
      }
    };

    loadPopular();
    return () => {
      cancelled = true;
    };
  }, [isOpen, state.popularLoaded]);

  // Cleanup search timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  // Debounced CivitAI search
  const handleSearchChange = useCallback(
    (query: string) => {
      dispatch({ type: 'SEARCH_QUERY_CHANGED', query });

      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }

      if (!query.trim()) {
        dispatch({ type: 'SEARCH_CLEARED' });
        return;
      }

      dispatch({ type: 'SEARCH_STARTED' });

      searchTimerRef.current = setTimeout(async () => {
        try {
          const response = await imageGenerationAPI.searchModels(query.trim());
          if (response.success && response.data) {
            const searchData = response.data.models;
            dispatch({
              type: 'SEARCH_RESULTS',
              models: Array.isArray(searchData) ? searchData : [],
            });
          } else {
            dispatch({
              type: 'SEARCH_ERROR',
              error:
                response.error ||
                t('image.model.searchError', { defaultValue: 'Failed to search models' }),
            });
          }
        } catch {
          dispatch({
            type: 'SEARCH_ERROR',
            error: t('image.model.searchError', { defaultValue: 'Failed to search models' }),
          });
        }
      }, 300);
    },
    [t]
  );

  const handleSelect = useCallback(
    (model: ImageModelInfo) => {
      if (model.source === 'civitai') return;
      onChange(model.id, model);
      close();
    },
    [onChange, close]
  );

  const handleImageBroken = useCallback((key: string) => {
    dispatch({ type: 'IMAGE_BROKEN', key });
  }, []);

  // Derived values
  const selectedValue = value || state.defaultModel;
  const selectedModel = Array.isArray(state.models)
    ? state.models.find((m) => m.id === selectedValue)
    : undefined;
  const selectedName = selectedModel?.name || selectedValue || '';

  if (!state.supportsModels && !state.isLoading) {
    return null;
  }

  if (state.isLoading) {
    return (
      <div className={`form-group ${className}`}>
        <label className="form-label flex items-center gap-2 mb-2">
          <Box className="h-4 w-4 flex-shrink-0" />
          <span>{t('image.model.label', { defaultValue: 'Checkpoint Model' })}</span>
        </label>
        <div className="flex items-center justify-center py-8 text-secondary-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          <span>{t('common.loading', { defaultValue: 'Loading...' })}</span>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className={`form-group ${className}`}>
        <label className="form-label flex items-center gap-2 mb-2">
          <Box className="h-4 w-4 flex-shrink-0" />
          <span>{t('image.model.label', { defaultValue: 'Checkpoint Model' })}</span>
        </label>
        <div className="p-4 bg-red-900/20 rounded-lg text-sm text-red-300">{state.error}</div>
      </div>
    );
  }

  if (state.models.length === 0) {
    return null;
  }

  return (
    <div className={`form-group ${className}`}>
      <label className="form-label flex items-center gap-2 mb-2">
        <Box className="h-4 w-4 flex-shrink-0" />
        <span>{t('image.model.label', { defaultValue: 'Checkpoint Model' })}</span>
      </label>

      {/* Dropdown trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        disabled={disabled}
        className={`w-full flex items-center justify-between min-h-[44px] px-3 py-2.5 rounded-lg border transition-all text-left ${
          isOpen
            ? 'border-primary-500 ring-2 ring-primary-500/30'
            : 'border-white/10 hover:border-white/20'
        } bg-black/20 text-white ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {selectedModel?.image_url && (
            <img
              src={selectedModel.image_url}
              alt=""
              className="w-6 h-6 rounded object-cover flex-shrink-0"
            />
          )}
          <span className="truncate text-sm">
            {selectedName || t('image.model.label', { defaultValue: 'Checkpoint Model' })}
          </span>
        </div>
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 text-white/50 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown panel - portaled to body to escape .app-content overflow clipping */}
      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            style={panelStyle}
            className="bg-[#1a1028] border border-white/10 rounded-lg shadow-2xl overflow-hidden"
          >
            <SearchPanel
              models={state.models}
              civitaiModels={state.civitaiModels}
              popularModels={state.popularModels}
              selectedValue={selectedValue}
              searchQuery={state.searchQuery}
              isSearching={state.isSearching}
              searchError={state.searchError}
              popularLoaded={state.popularLoaded}
              brokenImages={state.brokenImages}
              onSearchChange={handleSearchChange}
              onSelect={handleSelect}
              onImageBroken={handleImageBroken}
            />
          </div>,
          document.body
        )}

      {/* Selected model detail card */}
      {selectedModel && (selectedModel.image_url || selectedModel.description) && !isOpen && (
        <PreviewPanel
          model={selectedModel}
          brokenImages={state.brokenImages}
          onImageBroken={handleImageBroken}
        />
      )}
    </div>
  );
});

export default ImageModelSelector;
